/**
 * @Description 相机点位类，包括单点位类，点位集类（单例），编辑点位类
 * @Author iCEA
 * @Date 2020-10-29
 * @Version 1.0
 * @todo 点位集内的onClick事件需要修改mgraph
 */
import * as THREE from 'three'
import { MathUtils, Matrix4 } from 'three'
import { config } from './config'
import { Scene } from './Scene'
import { strToNumberArray } from './Utils'

export class CameraPoint {
  /**
   * 单个点位的构造函数
   * @param {object} pointMsg json配置文件内的points对象
   * @param {THREE.Texture} texture 单个点位所使用的纹理
   */
  constructor (pointMsg, texture) {
    /**
     * @type {number} 点位id
     */
    this.id = pointMsg.pointIndex
    /**
     * @type {THREE.Mesh} 点位Mesh
     */
    this.pointMesh = null

    // 位置信息
    /**
     * @type {THREE.Vector3} 点位位置信息转换为Vector3格式
     */
    this.position = new THREE.Vector3().fromArray(strToNumberArray(pointMsg.pointPosition))
    /**
     * @type {number} 点位网格的y轴位置（高度，一般是贴着地板）
     */
    this.meshY = pointMsg.pointFloor
    /**
     * @type {boolean} 点位是否可视
     */
    this.isAvailable = pointMsg.isVisible
    /**
     * @type {number} 点位类别（暂无应用）：0表示常规点 1表示入口点（不可关闭） 2表示关键点（不可关闭）
     */
    this.type = 1

    // TODO: 暂未注释
    this.correct = pointMsg.isCorrect

    this.rotation = new THREE.Quaternion().fromArray(strToNumberArray(pointMsg.pointForward))
    this.pointCorrect = new THREE.Euler().fromArray(strToNumberArray(pointMsg.pointCorrection).map(i => MathUtils.degToRad(i)))

    this.resetCorrectMat()
    this.initCameraPoint(texture)
  }

  resetCorrectMat () {
    // 版本问题修正
    if (new THREE.Matrix4().invert) {
      this.correctMat = new THREE.Matrix4().compose(
        this.position,
        this.rotation,
        new THREE.Vector3(1, 1, 1)
      ).multiply(new THREE.Matrix4().makeRotationFromEuler(this.pointCorrect)).invert()
    } else {
      this.correctMat = new THREE.Matrix4().getInverse(
        new THREE.Matrix4().compose(
          this.position,
          this.rotation,
          new THREE.Vector3(1, 1, 1)
        ).multiply(new THREE.Matrix4().makeRotationFromEuler(this.pointCorrect)))
    }
  }

  /**
   * 初始化一个点位
   * @param {THREE.Texture} texture 点位贴图纹理
   */
  initCameraPoint (texture) {
    const staticPointG = new THREE.PlaneBufferGeometry(256, 256)
    const staticPointM = new THREE.MeshBasicMaterial({ color: 0xffffff, map: texture, opacity: config.CPoint.NormalOpacity, transparent: true })
    this.pointMesh = new THREE.Mesh(staticPointG, staticPointM)
    staticPointG.dispose()
    staticPointM.dispose()
    this.pointMesh.position.set(
      this.position.x,
      this.meshY + 10,
      this.position.z
    )
    this.pointMesh.rotateX(-Math.PI / 2)
  }

  get highLight () {
    return this.pointMesh.material.opacity === config.CPoint.ActiveOpacity
  }

  set highLight (value) {
    this.pointMesh.material.opacity = value ? config.CPoint.ActiveOpacity : config.CPoint.NormalOpacity
  }
}

class CPointSet {
  /**
   * 构造函数
   * @param {object} settings setting.model_points
   * @param {object} points setting.model_points
   * @param {Scene} scene 场景实例
   */
  constructor (settings, points, scene) {
    /**
     * @type {boolean} 是否显示点位图标
     */
    this.showIcon = settings.isShowIcon
    const iconUrl = settings.isShowIcon ? settings.iconUrl : config.Default.LocationIcon

    /**
     * @type {boolean} 是否开启上下视角设置
     */
    this.angleLimit = settings.isLimitAngle
    /**
     * @type {boolean} 是否开启视角倾斜自动调整
     */
    this.autoCurrect = settings.isAutoCurrect
    /**
     * @type {number} 点位总数
     */
    this.numPoints = points.length
    /**
     * @type {CameraPoint[]} CameraPoint对象数组
     */
    this.CameraPoints = []
    /**
     * @type {number[]} 点位图，邻接矩阵，计算最短路径
     */
    this.MGraph = []
    for (let i = 0; i < points.length; ++i) {
      const a = []
      for (let j = 0; j < points.length; ++j) {
        a.push(settings.mgraph[i][j])
      }
      this.MGraph.push(a)
    }
    /**
     * @type {THREE.Texture} 点位贴图，根据icon_url加载图片，仅加载一次，全点位共用
     */
    this.iconTexture = new THREE.TextureLoader().load(iconUrl)

    this.initCameraPoints(points, this.iconTexture, scene)
  }

  /**
   * 初始化创建一群点位
   * @param {object} points 设置对象points
   * @param {THREE.Texture} texture 图标贴图纹理
   * @param {Scene} scene 场景
   */
  initCameraPoints (points, texture, scene) {
    this.iconGroup = new THREE.Group()
    for (const pd of points) {
      if (pd.isVisible) {
        const cp = new CameraPoint(pd, texture)
        this.CameraPoints.push(cp)
        this.iconGroup.add(cp.pointMesh)
      } else {
        this.disMGraph(pd.pointIndex)
      }
    }
    scene.scene.add(this.iconGroup)
  }

  findVisiable (src) {
    const ls = []
    for (let i = 0; i < this.numPoints; ++i) {
      if (i !== src && this.MGraph[src][i] !== config.CPoint.DistMax) {
        ls.push(i)
      }
    }
    return ls
  }

  /**
   * 根据点位id的不开启情况将mgraph对应的行列设置为65535，表示道路阻断
   * @param {number} id 点位id
   */
  disMGraph (id) {
    if (this.MGraph.length <= 1) return
    // 遍历id
    for (let i = 0; i < this.numPoints; ++i) {
      if (i === id) { // 找到了需要设置65535的行/列了
        for (let j = 0; j < this.numPoints; ++j) {
          if (i === j) { // 如果行列相等则置0
            this.MGraph[j][j] = 0
          } else { // 否则置65535
            this.MGraph[i][j] = config.CPoint.DistMax
            this.MGraph[j][i] = config.CPoint.DistMax
          }
        }
      }
    }
  }

  /**
   * 根据id信息获取点位对象
   * @param {number} id 点位id
   */
  getCameraPoint (id) {
    for (const p of this.CameraPoints) {
      if (p.id === id) {
        return p
      }
    }
  }

  /**
   * 寻找起止点最短路径，依赖邻接矩阵MGraph，路径结果填充到cameraRoute[]
   * @param {number} Vs 起点，相机当前id
   * @param {number} Vd 终点，目标点位id
   */
  findPath (Vs, Vd) {
    // Dijkstra 算法
    const inf = config.CPoint.DistMax

    const dist = []
    const mark = []
    const perIndex = []

    for (let v = 0; v < this.numPoints; v++) {
      mark[v] = false
      dist[v] = this.MGraph[Vs][v]
      perIndex[v] = Vs
    }

    mark[Vs] = true

    let min = inf
    let minIdx = Vs

    for (let i = 0; i < this.MGraph.length; i++) {
      min = inf
      for (let j = 0; j < this.MGraph.length; j++) {
        if (!mark[j] && dist[j] < min) {
          min = dist[j]
          minIdx = j
        }
      }

      mark[minIdx] = true

      for (let k = 0; k < this.MGraph.length; k++) {
        if (this.MGraph[minIdx][k] < inf && !mark[k] && (dist[k] > dist[minIdx] + this.MGraph[minIdx][k])) {
          dist[k] = dist[minIdx] + this.MGraph[minIdx][k]
          perIndex[k] = minIdx
        }
      }
    }

    const rls = []
    let idx = Vd
    while (idx !== Vs) {
      rls.push(idx)
      idx = perIndex[idx]
    }
    rls.push(Vs)
    rls.reverse()
    //console.log('最短路径：', rls.join('->'))
    return rls
  }
}

export {
  CPointSet
}
